// src/components/SalaryApprovals.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  AlertTitle,
  Divider,
  FormHelperText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  CardGiftcard as BonusIcon,
  Receipt as ReceiptIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import Sidebar from '../../../../components/sidebars/Sidebar_Gm';
import TopNav from '../../../../components/TopNav_hotelStaff';

// ==================== CONSTANTS ====================
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];
const MANAGER_ROLES = ['gm', 'manager', 'admin', 'hr'];

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize string input to prevent XSS attacks
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[<>]/g, '')
    .replace(/[&]/g, '&amp;')
    .replace(/["]/g, '&quot;')
    .replace(/[']/g, '&#x27;')
    .replace(/[/]/g, '&#x2F;');
};

/**
 * Format currency for Nigerian Naira
 */
const formatCurrency = (amount) => {
  const num = Number(amount);
  if (isNaN(num) || num === 0) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

/**
 * Format number with commas
 */
const formatNumber = (num) => {
  const n = Number(num);
  if (isNaN(n)) return '0';
  return new Intl.NumberFormat('en-NG').format(Math.round(n));
};

/**
 * Format date safely
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG');
  } catch (error) {
    console.error('[SalaryApprovals] Date formatting error:', error);
    return 'N/A';
  }
};

/**
 * Log security events
 */
const logSecurityEvent = (eventType, details) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  console.debug(`[SECURITY] ${eventType}:`, logEntry);
};

/**
 * Get request type configuration
 */
const getRequestTypeConfig = (request) => {
  if (!request) {
    return {
      type: 'unknown',
      label: 'Unknown',
      icon: <PendingIcon />,
      color: '#9e9e9e',
      bgColor: '#f5f5f5',
      amount: 0,
      reason: 'No reason provided',
      current: 0,
      showNewSalary: false,
      newSalary: null,
      priority: 2,
      isBlocked: false,
      blockedReason: null
    };
  }

  // Check if it's a bonus request
  if (request.adjustment_type === 'bonus' || request.is_bonus === 1) {
    const amount = Number(request.recent_bonus) || Number(request.requested_amount) || 0;
    return {
      type: 'bonus',
      label: 'Bonus',
      icon: <BonusIcon />,
      color: '#ff9800',
      bgColor: '#fff3e0',
      amount: amount,
      reason: sanitizeString(request.bonus_reason || request.reason_text || 'No reason provided'),
      current: Number(request.salary_amount) || 0,
      showNewSalary: false,
      newSalary: null,
      priority: request.priority || 2,
      isBlocked: request.is_blocked || false,
      blockedReason: request.blocked_reason || null
    };
  }
  
  // Check if it's a downgrade request
  if (request.adjustment_type === 'downgrade' || request.is_downgrade === 1) {
    const amount = Number(request.recent_downgrade) || Number(request.requested_amount) || 0;
    const currentSalary = Number(request.salary_amount) || Number(request.current_salary_from_table) || 0;
    const newSalary = request.new_salary || Math.max(0, currentSalary - amount);
    return {
      type: 'downgrade',
      label: 'Downgrade',
      icon: <TrendingDownIcon />,
      color: '#f44336',
      bgColor: '#ffebee',
      amount: amount,
      reason: sanitizeString(request.downgrade_reason || request.reason_text || 'No reason provided'),
      current: currentSalary,
      showNewSalary: true,
      newSalary: newSalary,
      priority: request.priority || 1,
      isBlocked: false,
      blockedReason: null
    };
  }
  
  // Default: increment request
  const amount = Number(request.recent_increment) || Number(request.requested_amount) || 0;
  const currentSalary = Number(request.salary_amount) || Number(request.current_salary_from_table) || 0;
  const newSalary = request.new_salary || (currentSalary + amount);
  return {
    type: 'increment',
    label: 'Increment',
    icon: <TrendingUpIcon />,
    color: '#4caf50',
    bgColor: '#e8f5e9',
    amount: amount,
    reason: sanitizeString(request.increment_reason || request.reason_text || 'No reason provided'),
    current: currentSalary,
    showNewSalary: true,
    newSalary: newSalary,
    priority: request.priority || 1,
    isBlocked: false,
    blockedReason: null
  };
};

// ==================== MAIN COMPONENT ====================
const SalaryApprovals = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data states
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedBonuses, setApprovedBonuses] = useState([]);
  const [allSalaries, setAllSalaries] = useState([]);
  const [stats, setStats] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('salary_desc');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [blockedBonuses, setBlockedBonuses] = useState([]);
  const [blockedBonusesCount, setBlockedBonusesCount] = useState(0);
  const [debugMode, setDebugMode] = useState(false);
  const [markingPaid, setMarkingPaid] = useState({});

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
    console.debug(`[SalaryApprovals] Sidebar toggled: ${!sidebarOpen}`);
  };

  // ==================== DERIVED VALUES ====================
  const uniqueDepartments = useMemo(() => {
    const depts = ['all', ...new Set(allSalaries.map(s => s.department).filter(Boolean))];
    console.debug(`[SalaryApprovals] Unique departments: ${depts.length - 1} found`);
    return depts;
  }, [allSalaries]);

  const filteredPendingRequests = useMemo(() => {
    let filtered = [...pendingRequests];
    
    if (requestTypeFilter !== 'all') {
      filtered = filtered.filter(req => {
        if (requestTypeFilter === 'increment') return req.adjustment_type === 'increment';
        if (requestTypeFilter === 'downgrade') return req.adjustment_type === 'downgrade';
        if (requestTypeFilter === 'bonus') return req.adjustment_type === 'bonus';
        return true;
      });
    }
    
    // Sort by priority (salary changes first, then bonuses)
    filtered.sort((a, b) => {
      const aPriority = a.priority || (a.adjustment_type === 'bonus' ? 2 : 1);
      const bPriority = b.priority || (b.adjustment_type === 'bonus' ? 2 : 1);
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      return new Date(a.request_date) - new Date(b.request_date);
    });
    
    console.debug(`[SalaryApprovals] Filtered pending requests: ${filtered.length}`);
    return filtered;
  }, [pendingRequests, requestTypeFilter]);

  const filteredSalaries = useMemo(() => {
    let filtered = [...allSalaries];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        (s.full_name || '').toLowerCase().includes(term) ||
        (s.phone || '').includes(term) ||
        (s.department || '').toLowerCase().includes(term) ||
        (s.email || '').toLowerCase().includes(term)
      );
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(s => s.department === departmentFilter);
    }
    
    switch (sortBy) {
      case 'salary_desc':
        filtered.sort((a, b) => (Number(b.salary_amount) || 0) - (Number(a.salary_amount) || 0));
        break;
      case 'salary_asc':
        filtered.sort((a, b) => (Number(a.salary_amount) || 0) - (Number(b.salary_amount) || 0));
        break;
      case 'name_asc':
        filtered.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        break;
      case 'name_desc':
        filtered.sort((a, b) => (b.full_name || '').localeCompare(a.full_name || ''));
        break;
      default:
        break;
    }
    
    console.debug(`[SalaryApprovals] Filtered salaries: ${filtered.length} of ${allSalaries.length}`);
    return filtered;
  }, [allSalaries, searchTerm, departmentFilter, sortBy]);

  // Statistics
  const pendingCount = filteredPendingRequests.length;
  const pendingIncrementCount = filteredPendingRequests.filter(r => r.adjustment_type === 'increment').length;
  const pendingDowngradeCount = filteredPendingRequests.filter(r => r.adjustment_type === 'downgrade').length;
  const pendingBonusCount = filteredPendingRequests.filter(r => r.adjustment_type === 'bonus').length;
  
  const totalIncrementAmount = filteredPendingRequests
    .filter(r => r.adjustment_type === 'increment')
    .reduce((sum, req) => sum + (Number(req.recent_increment) || Number(req.requested_amount) || 0), 0);
  const totalDowngradeAmount = filteredPendingRequests
    .filter(r => r.adjustment_type === 'downgrade')
    .reduce((sum, req) => sum + (Number(req.recent_downgrade) || Number(req.requested_amount) || 0), 0);
  const totalBonusAmount = filteredPendingRequests
    .filter(r => r.adjustment_type === 'bonus')
    .reduce((sum, req) => sum + (Number(req.recent_bonus) || Number(req.requested_amount) || 0), 0);
    
  const totalPayroll = Number(stats?.total_monthly_payroll) || 0;
  const averageSalary = Number(stats?.average_salary) || 0;
  const totalUnpaidBonus = approvedBonuses.reduce((sum, b) => sum + (Number(b.recent_bonus) || 0), 0);

  const pieChartData = departmentStats.map(dept => ({
    name: dept.department,
    value: Number(dept.total_salary) || 0,
    staffCount: dept.staff_count
  }));

  // ==================== API CALLS ====================
  const getApiBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    console.debug(`[SalaryApprovals] API Base URL: ${baseUrl || 'default'}`);
    return baseUrl;
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    console.debug('[SalaryApprovals] Fetching all data...');
    
    try {
      const baseUrl = getApiBaseUrl();
      const debugParam = debugMode ? '&debug=1' : '';
      
      // Fetch pending approvals with priority-based filtering
      const pendingUrl = `${baseUrl}/hotel/HR/update_salary.php?approval_status=pending${debugParam}`;
      console.debug(`[SalaryApprovals] GET: ${pendingUrl}`);
      
      const pendingRes = await fetch(pendingUrl);
      const pendingResult = await pendingRes.json();
      
      if (pendingResult.success) {
        console.debug(`[SalaryApprovals] Pending requests: ${pendingResult.data?.length || 0} total`);
        console.debug(`[SalaryApprovals] - Salary changes: ${pendingResult.salary_changes_count || 0}`);
        console.debug(`[SalaryApprovals] - Visible bonuses: ${pendingResult.bonuses_count || 0}`);
        console.debug(`[SalaryApprovals] - Blocked bonuses: ${pendingResult.blocked_bonuses_count || 0}`);
        
        setPendingRequests(pendingResult.data || []);
        setBlockedBonuses(pendingResult.blocked_bonuses || []);
        setBlockedBonusesCount(pendingResult.blocked_bonuses_count || 0);
      } else {
        console.error('[SalaryApprovals] Failed to fetch pending:', pendingResult.message);
        setSnackbar({ open: true, message: pendingResult.message || 'Failed to fetch pending requests', severity: 'error' });
      }
      
      // Fetch approved bonuses (unpaid)
      const bonusUrl = `${baseUrl}/hotel/HR/update_salary.php?approval_status=approved&request_type=bonus`;
      console.debug(`[SalaryApprovals] GET: ${bonusUrl}`);
      
      const bonusRes = await fetch(bonusUrl);
      const bonusResult = await bonusRes.json();
      
      if (bonusResult.success) {
        const unpaidBonuses = (bonusResult.data || []).filter(b => !b.is_paid && b.status === 'Active');
        setApprovedBonuses(unpaidBonuses);
        console.debug(`[SalaryApprovals] Approved unpaid bonuses: ${unpaidBonuses.length}`);
      }
      
      // Fetch all salaries for active staff
      const allUrl = `${baseUrl}/hotel/HR/update_salary.php?type=all`;
      console.debug(`[SalaryApprovals] GET: ${allUrl}`);
      
      const allRes = await fetch(allUrl);
      const allResult = await allRes.json();
      
      if (allResult.success) {
        setAllSalaries(allResult.data || []);
        console.debug(`[SalaryApprovals] Staff salaries: ${allResult.data?.length || 0}`);
      }
      
      // Fetch statistics
      const statsUrl = `${baseUrl}/hotel/HR/update_salary.php?type=stats`;
      console.debug(`[SalaryApprovals] GET: ${statsUrl}`);
      
      const statsRes = await fetch(statsUrl);
      const statsResult = await statsRes.json();
      
      if (statsResult.success) {
        setStats(statsResult.stats || {});
        setDepartmentStats(statsResult.department_stats || []);
        console.debug('[SalaryApprovals] Stats loaded');
      }
      
      const duration = (performance.now() - startTime).toFixed(2);
      console.debug(`[SalaryApprovals] Fetch completed in ${duration}ms`);
      
    } catch (err) {
      console.error('[SalaryApprovals] Error fetching data:', err);
      setSnackbar({ open: true, message: 'Failed to load data: ' + err.message, severity: 'error' });
      logSecurityEvent('FETCH_DATA_ERROR', { error: err.message });
    } finally {
      setLoading(false);
    }
  }, [debugMode]);

  // ==================== HANDLER FUNCTIONS ====================
  const handleApprove = async () => {
    if (!selectedRequest) {
      console.warn('[SalaryApprovals] No request selected for approval');
      return;
    }
    
    logSecurityEvent('APPROVAL_ATTEMPT', { 
      id: selectedRequest.id, 
      type: selectedRequest.adjustment_type,
      amount: selectedRequest.recent_bonus || selectedRequest.requested_amount 
    });
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const baseUrl = getApiBaseUrl();
      const payload = {
        id: selectedRequest.id,
        approval_status: 'approved',
        approved_by: localStorage.getItem('userName') || 'Manager',
        approval_comment: sanitizeString(approvalComment)
      };
      
      console.debug(`[SalaryApprovals] PUT approve request:`, payload);
      
      const response = await fetch(`${baseUrl}/hotel/HR/update_salary.php`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      const duration = (performance.now() - startTime).toFixed(2);
      console.debug(`[SalaryApprovals] Approve response in ${duration}ms:`, result);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setApproveDialogOpen(false);
        setApprovalComment('');
        setSelectedRequest(null);
        await fetchAllData();
        logSecurityEvent('APPROVAL_SUCCESS', { id: selectedRequest.id });
      } else {
        console.error('[SalaryApprovals] Approval failed:', result.message);
        setSnackbar({ open: true, message: result.message, severity: 'error' });
        logSecurityEvent('APPROVAL_FAILED', { id: selectedRequest.id, error: result.message });
      }
    } catch (err) {
      console.error('[SalaryApprovals] Approve error:', err);
      setSnackbar({ open: true, message: 'Failed to approve request: ' + err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) {
      console.warn('[SalaryApprovals] No request selected for rejection');
      return;
    }
    
    if (!approvalComment || approvalComment.trim().length < 3) {
      setSnackbar({ open: true, message: 'Please provide a reason for rejection (minimum 3 characters)', severity: 'warning' });
      return;
    }
    
    logSecurityEvent('REJECTION_ATTEMPT', { 
      id: selectedRequest.id, 
      type: selectedRequest.adjustment_type,
      reason: approvalComment 
    });
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const baseUrl = getApiBaseUrl();
      const payload = {
        id: selectedRequest.id,
        approval_status: 'rejected',
        approved_by: localStorage.getItem('userName') || 'Manager',
        approval_comment: sanitizeString(approvalComment)
      };
      
      console.debug(`[SalaryApprovals] PUT reject request:`, payload);
      
      const response = await fetch(`${baseUrl}/hotel/HR/update_salary.php`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      const duration = (performance.now() - startTime).toFixed(2);
      console.debug(`[SalaryApprovals] Reject response in ${duration}ms:`, result);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setRejectDialogOpen(false);
        setApprovalComment('');
        setSelectedRequest(null);
        await fetchAllData();
        logSecurityEvent('REJECTION_SUCCESS', { id: selectedRequest.id });
      } else {
        console.error('[SalaryApprovals] Rejection failed:', result.message);
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[SalaryApprovals] Reject error:', err);
      setSnackbar({ open: true, message: 'Failed to reject request: ' + err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkBonusPaid = async (bonus) => {
    logSecurityEvent('MARK_PAID_ATTEMPT', { id: bonus.id, amount: bonus.recent_bonus });
    
    const confirmMark = window.confirm(
      `Are you sure you want to mark this bonus of ${formatCurrency(bonus.recent_bonus)} for ${bonus.full_name} as PAID?\n\nThis action cannot be undone.`
    );
    if (!confirmMark) return;
    
    setMarkingPaid(prev => ({ ...prev, [bonus.id]: true }));
    const startTime = performance.now();
    
    try {
      const baseUrl = getApiBaseUrl();
      const payload = {
        id: bonus.id,
        paid_by: localStorage.getItem('userName') || 'Manager'
      };
      
      console.debug(`[SalaryApprovals] PATCH mark bonus paid:`, payload);
      
      const response = await fetch(`${baseUrl}/hotel/HR/update_salary.php`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      const duration = (performance.now() - startTime).toFixed(2);
      console.debug(`[SalaryApprovals] Mark paid response in ${duration}ms:`, result);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        await fetchAllData();
        logSecurityEvent('MARK_PAID_SUCCESS', { id: bonus.id });
      } else {
        console.error('[SalaryApprovals] Mark paid failed:', result.message);
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[SalaryApprovals] Mark paid error:', err);
      setSnackbar({ open: true, message: 'Failed to mark bonus as paid: ' + err.message, severity: 'error' });
    } finally {
      setMarkingPaid(prev => ({ ...prev, [bonus.id]: false }));
    }
  };

  const exportToCSV = () => {
    logSecurityEvent('EXPORT_ATTEMPT', { type: 'salaries_csv' });
    
    try {
      const headers = ['Full Name', 'Department', 'Phone', 'Email', 'Salary Amount', 'Bank', 'Account Number', 'Status'];
      const csvRows = [headers];
      
      filteredSalaries.forEach(s => {
        csvRows.push([
          sanitizeString(s.full_name || ''),
          sanitizeString(s.department || ''),
          s.phone || '',
          sanitizeString(s.email || ''),
          s.salary_amount || 0,
          sanitizeString(s.bank_name || ''),
          s.account_number || '',
          s.status || 'Active'
        ]);
      });
      
      const csvContent = csvRows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salaries_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: `CSV exported successfully! ${filteredSalaries.length} records.`, severity: 'success' });
      logSecurityEvent('EXPORT_SUCCESS', { record_count: filteredSalaries.length });
    } catch (err) {
      console.error('[SalaryApprovals] Export error:', err);
      setSnackbar({ open: true, message: 'Failed to export CSV: ' + err.message, severity: 'error' });
    }
  };

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    const savedDebugMode = localStorage.getItem('salary_debug_mode');
    if (savedDebugMode === 'true') {
      setDebugMode(true);
      console.debug('[SalaryApprovals] Debug mode enabled');
    }
    
    fetchAllData();
  }, [fetchAllData]);

  // ==================== COMPONENT RENDER ====================
  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{ 
      borderRadius: 3, 
      height: '100%',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1, color: color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && allSalaries.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading salary data...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ 
        flexGrow: 1, 
        p: 3, 
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5, 
        maxWidth: '1600px', 
        mx: 'auto', 
        width: '100%', 
        overflow: 'auto', 
        height: '100vh',
        transition: 'margin-left 0.3s ease-in-out',
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            window.location.href = '/';
          }}
          onNotificationClick={() => console.debug('[SalaryApprovals] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header Section */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, 
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
            borderRadius: '50%',
          }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Salary Management Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, fontFamily: 'Times New Roman, serif' }}>
                Manage increments, downgrades, bonuses, and payroll analytics
              </Typography>
              {blockedBonusesCount > 0 && (
                <Typography variant="caption" sx={{ opacity: 0.9, mt: 0.5, display: 'block' }}>
                  ⚠️ {blockedBonusesCount} bonus{blockedBonusesCount !== 1 ? 'es are' : ' is'} blocked pending salary approval
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} 
                onClick={fetchAllData} 
                disabled={loading}
                sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
              >
                {loading ? 'Loading...' : 'Refresh Data'}
              </Button>
              {debugMode && (
                <Chip 
                  label="DEBUG MODE" 
                  size="small" 
                  sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 'bold' }}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* Stats Cards Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Pending" value={pendingCount} subtitle="Awaiting approval" color="#ff9800" icon={PendingIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Pending Increments" value={pendingIncrementCount} subtitle={formatCurrency(totalIncrementAmount)} color="#4caf50" icon={TrendingUpIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Pending Downgrades" value={pendingDowngradeCount} subtitle={formatCurrency(totalDowngradeAmount)} color="#f44336" icon={TrendingDownIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Pending Bonuses" value={pendingBonusCount} subtitle={formatCurrency(totalBonusAmount)} color="#ff9800" icon={BonusIcon} />
          </Grid>
        </Grid>

        {/* Blocked Bonuses Warning Banner */}
        {blockedBonusesCount > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3, borderRadius: 2 }}
            icon={<BlockIcon />}
          >
            <AlertTitle sx={{ fontWeight: 'bold' }}>
              ⚠️ {blockedBonusesCount} Bonus {blockedBonusesCount !== 1 ? 'Requests are' : 'Request is'} Blocked
            </AlertTitle>
            <Typography variant="body2">
              {blockedBonusesCount} bonus {blockedBonusesCount !== 1 ? 'requests have been' : 'request has been'} automatically hidden because 
              {blockedBonusesCount !== 1 ? ' they depend on' : ' it depends on'} pending salary {blockedBonusesCount !== 1 ? 'changes' : 'change'}. 
              Please approve or reject the salary {blockedBonusesCount !== 1 ? 'changes' : 'change'} first, then the bonus{blockedBonusesCount !== 1 ? 'es' : ''} will become available for approval.
            </Typography>
            {debugMode && blockedBonuses.length > 0 && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  <strong>Debug Info:</strong>
                  {blockedBonuses.map((bonus, idx) => (
                    <div key={idx}>• {bonus.full_name}: ₦{bonus.recent_bonus} - {bonus.blocked_reason}</div>
                  ))}
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              console.debug(`[SalaryApprovals] Tab changed to: ${newValue}`);
              setActiveTab(newValue);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': { py: 2, fontSize: '1rem', fontWeight: 'bold', fontFamily: 'Times New Roman, serif', textTransform: 'none' },
              '& .Mui-selected': { color: theme.palette.primary.main },
              '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main }
            }}
          >
            <Tab icon={<PendingIcon />} iconPosition="start" label={`Pending Approvals (${pendingCount})`} />
            <Tab icon={<BonusIcon />} iconPosition="start" label={`Unpaid Bonuses (${approvedBonuses.length})`} />
            <Tab icon={<MoneyIcon />} iconPosition="start" label={`Staff Salaries (${allSalaries.length})`} />
            <Tab icon={<AssessmentIcon />} iconPosition="start" label="Payroll Analytics" />
          </Tabs>
        </Paper>

        {/* ==================== TAB 0: PENDING APPROVALS ==================== */}
        {activeTab === 0 && (
          <>
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight="bold">Filter by type:</Typography>
                  <ToggleButtonGroup
                    value={requestTypeFilter}
                    exclusive
                    onChange={(e, newVal) => {
                      if (newVal) {
                        console.debug(`[SalaryApprovals] Filter changed to: ${newVal}`);
                        setRequestTypeFilter(newVal);
                      }
                    }}
                    size="small"
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="increment">Increments</ToggleButton>
                    <ToggleButton value="downgrade">Downgrades</ToggleButton>
                    <ToggleButton value="bonus">Bonuses</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {blockedBonusesCount > 0 && (
                  <Chip 
                    icon={<BlockIcon />} 
                    label={`${blockedBonusesCount} blocked`} 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
              <TableContainer sx={{ 
                maxHeight: 'calc(100vh - 380px)',
                minHeight: '500px',
                overflow: 'auto',
                '& .MuiTable-root': { minWidth: 1200 }
              }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Staff Member</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Request Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Current Salary</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Requested Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>New/Impact</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Request Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Reason</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPendingRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                            No pending requests
                            {blockedBonusesCount > 0 && ` (${blockedBonusesCount} bonuses are blocked pending salary changes)`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPendingRequests.map((request) => {
                        const config = getRequestTypeConfig(request);
                        return (
                          <TableRow 
                            key={request.id} 
                            hover
                            sx={{ 
                              bgcolor: config.priority === 1 ? alpha('#4caf50', 0.05) : 'inherit',
                              '&:hover': { bgcolor: config.priority === 1 ? alpha('#4caf50', 0.1) : alpha('#000', 0.04) },
                              height: '72px'
                            }}
                          >
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: config.color, width: 40, height: 40 }}>{config.icon}</Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'Times New Roman, serif' }}>
                                    {sanitizeString(request.full_name) || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontFamily: 'Times New Roman, serif' }}>
                                    {request.email || request.phone}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <Chip size="small" label={sanitizeString(request.department) || 'N/A'} variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <Chip 
                                label={config.label} 
                                size="small" 
                                sx={{ 
                                  bgcolor: config.color, 
                                  color: 'white', 
                                  fontWeight: 'bold',
                                  ...(config.priority === 1 && { 
                                    border: '2px solid #4caf50',
                                    boxShadow: '0 0 4px rgba(76,175,80,0.5)'
                                  })
                                }} 
                              />
                              {config.priority === 1 && (
                                <Typography variant="caption" sx={{ ml: 1, color: '#4caf50', fontWeight: 'bold' }}>
                                  (Priority)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle', fontFamily: 'monospace' }}>
                              {formatCurrency(config.current)}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: config.color }}>
                                {config.type === 'downgrade' ? '-' : '+'}{formatCurrency(config.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              {config.showNewSalary ? (
                                <Typography variant="body2" fontWeight="bold" color="#C84646" sx={{ fontFamily: 'monospace' }}>
                                  {formatCurrency(config.newSalary)}
                                </Typography>
                              ) : (
                                <Chip size="small" label="One-time payment" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {formatDate(request.request_date)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              <Tooltip title={config.reason}>
                                <Chip 
                                  size="small" 
                                  label={config.reason.length > 35 ? config.reason.substring(0, 35) + '...' : config.reason} 
                                  variant="outlined" 
                                  icon={<CommentIcon sx={{ fontSize: 14 }} />} 
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                              <Tooltip title="Approve">
                                <IconButton 
                                  size="small" 
                                  onClick={() => { 
                                    console.debug(`[SalaryApprovals] Opening approve dialog for request ${request.id}`);
                                    setSelectedRequest(request); 
                                    setApproveDialogOpen(true); 
                                  }} 
                                  sx={{ color: '#4caf50' }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton 
                                  size="small" 
                                  onClick={() => { 
                                    console.debug(`[SalaryApprovals] Opening reject dialog for request ${request.id}`);
                                    setSelectedRequest(request); 
                                    setRejectDialogOpen(true); 
                                  }} 
                                  sx={{ color: '#f44336' }}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Footer summary */}
              {filteredPendingRequests.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#fafafa', 
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Total Pending:</strong> {filteredPendingRequests.length}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ color: '#4caf50', fontFamily: 'Times New Roman, serif' }}>
                      <strong>Increments:</strong> {pendingIncrementCount} ({formatCurrency(totalIncrementAmount)})
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f44336', fontFamily: 'Times New Roman, serif' }}>
                      <strong>Downgrades:</strong> {pendingDowngradeCount} ({formatCurrency(totalDowngradeAmount)})
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ff9800', fontFamily: 'Times New Roman, serif' }}>
                      <strong>Bonuses:</strong> {pendingBonusCount} ({formatCurrency(totalBonusAmount)})
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ==================== TAB 1: UNPAID BONUSES ==================== */}
        {activeTab === 1 && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard title="Approved Bonuses" value={approvedBonuses.length} subtitle="Awaiting payment" color="#ff9800" icon={BonusIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard title="Total Bonus Amount" value={formatCurrency(totalUnpaidBonus)} subtitle="To be disbursed" color="#ff9800" icon={MoneyIcon} />
              </Grid>
            </Grid>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
              <TableContainer sx={{ 
                maxHeight: 'calc(100vh - 380px)',
                minHeight: '500px',
                overflow: 'auto',
                '& .MuiTable-root': { minWidth: 900 }
              }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Staff Member</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Bonus Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Approval Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Reason</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvedBonuses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                            No unpaid bonuses
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      approvedBonuses.map((bonus) => (
                        <TableRow key={bonus.id} hover sx={{ height: '72px' }}>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: '#ff9800', width: 40, height: 40 }}><BonusIcon /></Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'Times New Roman, serif' }}>
                                  {sanitizeString(bonus.full_name) || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontFamily: 'Times New Roman, serif' }}>
                                  {bonus.email || bonus.phone}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Chip size="small" label={sanitizeString(bonus.department) || 'N/A'} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" fontWeight="bold" color="#ff9800" sx={{ fontFamily: 'Times New Roman, serif' }}>
                              {formatCurrency(bonus.recent_bonus)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {formatDate(bonus.approved_date)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Tooltip title={sanitizeString(bonus.bonus_reason) || 'No reason'}>
                              <Chip 
                                size="small" 
                                label={(sanitizeString(bonus.bonus_reason) || 'No reason').substring(0, 30)} 
                                variant="outlined" 
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={markingPaid[bonus.id] ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
                              onClick={() => handleMarkBonusPaid(bonus)}
                              disabled={markingPaid[bonus.id]}
                              sx={{ 
                                bgcolor: '#4caf50', 
                                '&:hover': { bgcolor: '#45a049' },
                                textTransform: 'none',
                                fontFamily: 'Times New Roman, serif'
                              }}
                            >
                              {markingPaid[bonus.id] ? 'Processing...' : 'Mark as Paid'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {approvedBonuses.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#fafafa', 
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Total Unpaid:</strong> {formatCurrency(totalUnpaidBonus)} ({approvedBonuses.length} bonuses)
                  </Typography>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ==================== TAB 2: STAFF SALARIES ==================== */}
        {activeTab === 2 && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Staff" value={formatNumber(allSalaries.length)} color="#1976d2" icon={PersonIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Monthly Payroll" value={formatCurrency(totalPayroll)} color="#4caf50" icon={MoneyIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Average Salary" value={formatCurrency(averageSalary)} color="#ff9800" icon={AssessmentIcon} />
              </Grid>
            </Grid>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search by name, department, phone, or email..."
                  value={searchTerm}
                  onChange={(e) => {
                    console.debug(`[SalaryApprovals] Search term: ${e.target.value}`);
                    setSearchTerm(e.target.value);
                  }}
                  sx={{ flex: 2, minWidth: 200 }}
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  }}
                  inputProps={{ style: { fontFamily: 'Times New Roman, serif' } }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Department</InputLabel>
                  <Select 
                    value={departmentFilter} 
                    onChange={(e) => {
                      console.debug(`[SalaryApprovals] Department filter: ${e.target.value}`);
                      setDepartmentFilter(e.target.value);
                    }} 
                    label="Department"
                    sx={{ fontFamily: 'Times New Roman, serif' }}
                  >
                    {uniqueDepartments.map((dept, idx) => (
                      <MenuItem key={idx} value={dept} sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {dept === 'all' ? 'All Departments' : sanitizeString(dept)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Sort By</InputLabel>
                  <Select 
                    value={sortBy} 
                    onChange={(e) => {
                      console.debug(`[SalaryApprovals] Sort by: ${e.target.value}`);
                      setSortBy(e.target.value);
                    }} 
                    label="Sort By"
                    sx={{ fontFamily: 'Times New Roman, serif' }}
                  >
                    <MenuItem value="salary_desc">Highest Salary First</MenuItem>
                    <MenuItem value="salary_asc">Lowest Salary First</MenuItem>
                    <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                    <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                  </Select>
                </FormControl>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />} 
                  onClick={exportToCSV}
                  sx={{ fontFamily: 'Times New Roman, serif', textTransform: 'none' }}
                >
                  Export CSV
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
              <TableContainer sx={{ 
                maxHeight: 'calc(100vh - 480px)',
                minHeight: '450px',
                overflow: 'auto',
                '& .MuiTable-root': { minWidth: 1000 }
              }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif', width: 50 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Staff Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Email</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Salary Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Bank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Account Number</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSalaries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                          <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                            No salary records found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSalaries.map((staff, index) => (
                        <TableRow key={staff.id} hover sx={{ height: '68px' }}>
                          <TableCell sx={{ verticalAlign: 'middle', fontFamily: 'Times New Roman, serif' }}>{index + 1}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: '#C84646', width: 36, height: 36 }}>
                                <PersonIcon sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'Times New Roman, serif' }}>
                                  {sanitizeString(staff.full_name) || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                                  ID: {staff.id || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Chip size="small" label={sanitizeString(staff.department) || 'N/A'} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle', fontFamily: 'monospace' }}>{staff.phone || 'N/A'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {sanitizeString(staff.email) || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" fontWeight="bold" color="#C84646" sx={{ fontFamily: 'monospace' }}>
                              {formatCurrency(staff.salary_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            {staff.bank_name ? (
                              <Chip size="small" label={sanitizeString(staff.bank_name)} variant="outlined" />
                            ) : (
                              <Chip size="small" label="Not set" variant="outlined" color="warning" />
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle', fontFamily: 'monospace' }}>
                            {staff.account_number || 'Not set'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {filteredSalaries.length > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#fafafa', 
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Showing:</strong> {filteredSalaries.length} of {allSalaries.length} staff
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif', color: '#4caf50' }}>
                    <strong>Total Monthly Payroll:</strong> {formatCurrency(filteredSalaries.reduce((sum, s) => sum + (Number(s.salary_amount) || 0), 0))}
                  </Typography>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ==================== TAB 3: PAYROLL ANALYTICS ==================== */}
        {activeTab === 3 && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                    Payroll Distribution by Department
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={departmentStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="total_salary" fill="#C84646" name="Total Salary" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                    Staff Count by Department
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={departmentStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="staff_count" fill="#1976d2" name="Staff Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                    Salary Distribution Percentage
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie 
                        data={pieChartData} 
                        cx="50%" 
                        cy="50%" 
                        innerRadius={60} 
                        outerRadius={100} 
                        dataKey="value" 
                        label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                    Department Statistics
                  </Typography>
                  <TableContainer sx={{ maxHeight: 350 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Department</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Staff Count</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Total Salary</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>Average Salary</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {departmentStats.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                              No department data available
                            </TableCell>
                          </TableRow>
                        ) : (
                          departmentStats.map((dept, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{sanitizeString(dept.department)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif' }}>{formatNumber(dept.staff_count)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif' }}>{formatCurrency(dept.total_salary)}</TableCell>
                              <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif' }}>{formatCurrency(dept.avg_salary)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>

            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                Payroll Summary
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#e8f5e9', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>Total Monthly Payroll</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#4caf50" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {formatCurrency(totalPayroll)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>Annual Payroll Estimate</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#2196f3" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {formatCurrency(totalPayroll * 12)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>Pending Amount</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#ff9800" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {formatCurrency(totalIncrementAmount + totalBonusAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#f3e5f5', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>Highest Paid Dept</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#9c27b0" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        {departmentStats.length > 0 ? sanitizeString(departmentStats[0]?.department) : 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            © {new Date().getFullYear()} Elegantè IT - Salary Management System. All rights reserved.
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            Version 5.0 | Secure Salary Management System
          </Typography>
        </footer>
      </Box>

      {/* Approve Dialog */}
      <Dialog 
        open={approveDialogOpen} 
        onClose={() => {
          console.debug('[SalaryApprovals] Closing approve dialog');
          setApproveDialogOpen(false);
          setApprovalComment('');
          setSelectedRequest(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: selectedRequest ? getRequestTypeConfig(selectedRequest).color : '#4caf50', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon /> 
            Approve {selectedRequest ? getRequestTypeConfig(selectedRequest).label : 'Request'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedRequest && (() => {
              const config = getRequestTypeConfig(selectedRequest);
              return (
                <>
                  <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Staff:</strong> {sanitizeString(selectedRequest.full_name)}<br />
                    <strong>Department:</strong> {sanitizeString(selectedRequest.department)}<br />
                    <strong>Request Type:</strong> {config.label}<br />
                    <strong>Current Salary:</strong> {formatCurrency(config.current)}<br />
                    <strong>Requested {config.label}:</strong> {formatCurrency(config.amount)}<br />
                    {config.showNewSalary && (
                      <><strong>New Salary:</strong> {formatCurrency(config.newSalary)}</>
                    )}
                    {!config.showNewSalary && (
                      <><strong>Note:</strong> This is a one-time bonus payment that does not affect regular salary.</>
                    )}
                  </Alert>
                  {config.reason && config.reason !== 'No reason provided' && (
                    <Alert severity="warning" sx={{ fontFamily: 'Times New Roman, serif' }}>
                      <strong>Reason:</strong> {config.reason}
                    </Alert>
                  )}
                  <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Approving Officer:</strong> {localStorage.getItem('userName') || 'Manager'}
                  </Alert>
                  <TextField 
                    label="Approval Comment (Optional)" 
                    multiline 
                    rows={3} 
                    fullWidth 
                    value={approvalComment} 
                    onChange={(e) => setApprovalComment(e.target.value)} 
                    placeholder="Add any comments about this approval..."
                    inputProps={{ style: { fontFamily: 'Times New Roman, serif' } }}
                  />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} sx={{ fontFamily: 'Times New Roman, serif' }}>Cancel</Button>
          <Button 
            onClick={handleApprove} 
            variant="contained" 
            sx={{ bgcolor: selectedRequest ? getRequestTypeConfig(selectedRequest).color : '#4caf50', fontFamily: 'Times New Roman, serif' }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog 
        open={rejectDialogOpen} 
        onClose={() => {
          console.debug('[SalaryApprovals] Closing reject dialog');
          setRejectDialogOpen(false);
          setApprovalComment('');
          setSelectedRequest(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon /> 
            Reject {selectedRequest ? getRequestTypeConfig(selectedRequest).label : 'Request'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedRequest && (() => {
              const config = getRequestTypeConfig(selectedRequest);
              return (
                <>
                  <Alert severity="warning" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Staff:</strong> {sanitizeString(selectedRequest.full_name)}<br />
                    <strong>Request Type:</strong> {config.label}<br />
                    <strong>Requested Amount:</strong> {formatCurrency(config.amount)}<br />
                    {config.reason && config.reason !== 'No reason provided' && (
                      <><strong>Reason:</strong> {config.reason}<br /></>
                    )}
                  </Alert>
                  <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Rejecting Officer:</strong> {localStorage.getItem('userName') || 'Manager'}
                  </Alert>
                  <TextField 
                    label="Rejection Reason" 
                    multiline 
                    rows={3} 
                    fullWidth 
                    required 
                    value={approvalComment} 
                    onChange={(e) => setApprovalComment(e.target.value)} 
                    placeholder="Please provide a reason for rejection..."
                    error={approvalComment.length > 0 && approvalComment.length < 3}
                    helperText={approvalComment.length > 0 && approvalComment.length < 3 ? 'Please provide at least 3 characters' : ''}
                    inputProps={{ style: { fontFamily: 'Times New Roman, serif' } }}
                  />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} sx={{ fontFamily: 'Times New Roman, serif' }}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            variant="contained" 
            sx={{ bgcolor: '#f44336', fontFamily: 'Times New Roman, serif' }}
            disabled={loading || !approvalComment || approvalComment.length < 3}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ fontFamily: 'Times New Roman, serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalaryApprovals;